import React from 'react';
import { Project } from '../types/Project';
import { useLanguage } from '../contexts/LanguageContext';

interface ProjectCardProps {
  project: Project;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project }) => {
  const { t } = useLanguage(); // Call the hook at the top level and destructure what you need
  
  // Function to determine language emoji
  const languageEmoji = (lang: string) => {
    if (lang === 'pl') {
      return '🇵🇱';
    } else if (lang === 'en') {
      return '🇺🇸';
    } else if (lang === 'en pl') {
      return '🇺🇸 🇵🇱';
    } else {
      return '–';
    }
  };

  // Function to determine language tooltip text
  const languageEmojiTitle = (projectLang: string) => {
    if (projectLang === 'pl') {
      return t('projects', 'projectInPolish');
    } else if (projectLang === 'en') {
      return t('projects', 'projectInEnglish');
    } else if (projectLang === 'en pl') {
      return t('projects', 'projectInBothLang');
    } else {
      return t('projects', 'projectInNoLang');
    }
  };

  return (
    <div className="project-card">
      {/* Language indicator */}
      <span 
        className="lang" 
        title={languageEmojiTitle(project.lang)}
      >
        {languageEmoji(project.lang)}
      </span>
      
      {/* Date */}
      <time dateTime={project.date}>{project['date-word']}</time>
      
      {/* Project title */}
      <a href={project.demo || '#'} className="project-title">
        {project.title}
      </a>
      
      {/* Description */}
      <h4>{project.description}</h4>
      
      {/* Action buttons */}
      <div className="links anchored">
        {project.demo && (
          <a href={project.demo} className="button small flex" target="_blank" rel="noopener noreferrer">
            {t('common', 'demo')}
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 24 24" 
              style={{width: '0.75rem', height: '0.75rem', fill: 'currentColor'}}
            >
              <g data-name="Layer 2">
                <g data-name="external-link">
                  <rect width="24" height="24" opacity="0"></rect>
                  <path d="M20 11a1 1 0 0 0-1 1v6a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h6a1 1 0 0 0 0-2H6a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3h12a3 3 0 0 0 3-3v-6a1 1 0 0 0-1-1z"></path>
                  <path d="M16 5h1.58l-6.29 6.28a1 1 0 0 0 0 1.42a1 1 0 0 0 1.42 0L19 6.42V8a1 1 0 0 0 1 1 1 1 0 0 0 1-1V4a1 1 0 0 0-1-1h-4a1 1 0 0 0 0 2z"></path>
                </g>
              </g>
            </svg>
          </a>
        )}
        {project.source && (
          <a href={project.source} className="button small flex" target="_blank" rel="noopener noreferrer">
            {t('common', 'source')}
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 24 24" 
              style={{width: '0.75rem', height: '0.75rem', fill: 'currentColor'}}
            >
              <g data-name="Layer 2">
                <g data-name="external-link">
                  <rect width="24" height="24" opacity="0"></rect>
                  <path d="M20 11a1 1 0 0 0-1 1v6a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h6a1 1 0 0 0 0-2H6a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3h12a3 3 0 0 0 3-3v-6a1 1 0 0 0-1-1z"></path>
                  <path d="M16 5h1.58l-6.29 6.28a1 1 0 0 0 0 1.42 1 1 0 0 0 1.42 0L19 6.42V8a1 1 0 0 0 1 1 1 1 0 0 0 1-1V4a1 1 0 0 0-1-1h-4a1 1 0 0 0 0 2z"></path>
                </g>
              </g>
            </svg>
          </a>
        )}
        {project.seeMore && (
          <a href={project.seeMore} className="button small flex" target="_blank" rel="noopener noreferrer">
            {t('common', 'seeMore')}
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 24 24" 
              style={{width: '0.75rem', height: '0.75rem', fill: 'currentColor'}}
            >
              <g data-name="Layer 2">
                <g data-name="external-link">
                  <rect width="24" height="24" opacity="0"></rect>
                  <path d="M20 11a1 1 0 0 0-1 1v6a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h6a1 1 0 0 0 0-2H6a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3h12a3 3 0 0 0 3-3v-6a1 1 0 0 0-1-1z"></path>
                  <path d="M16 5h1.58l-6.29 6.28a1 1 0 0 0 0 1.42 1 1 0 0 0 1.42 0L19 6.42V8a1 1 0 0 0 1 1 1 1 0 0 0 1-1V4a1 1 0 0 0-1-1h-4a1 1 0 0 0 0 2z"></path>
                </g>
              </g>
            </svg>
          </a>
        )}
        
      </div>
      
      {/* Compatibility icons */}
      {project.pc && (
          <img src="/images/pc.svg" className="compatibility" alt={t('projects', 'pcCompatible')} title={t('projects', 'pcCompatible')} />
      )}
      {project.mobile && (
          <img src="/images/mobile.svg" className="compatibility mobile" alt={t('projects', 'mobileCompatible')} title={t('projects', 'mobileCompatible')} />
      )}
    </div>
  );
};

export default ProjectCard;